import { DateTime } from 'luxon';
import { convertFromCents, formatDateForFilters, formatNumberToMoney, formatPhoneNumber } from '~/utils/helpers';
import {
  effectiveDisplayOrder,
  BLOCK_TEXT_TYPE,
  blockTextContent,
  CHECKBOX_ON_VALUE,
  isCheckboxField,
  isCheckedAnswer,
  isDisplayOnlyField,
  isSignatureField,
  type CustomForm,
  type CustomFormField,
  type SignatureAnswer,
} from '~/composables/customForms/types';
import { buildMapperFields, parseApplicationQuestionKey, type MapperField } from '~/composables/customForms/pdfMapperFields';
import type { FormFieldFormStep } from '~/composables/types/DynamicForm';

/**
 * Turns an application (GET /applications/{id}) + a custom form (GET /custom-forms/{id}) into the
 * values shown on the form summary (generic PDF) or written on the uploaded template (custom PDF).
 *
 * The applicant's answers to the custom form live in the application step whose `stepId` equals
 * the form's `formStepId`; `step.data[0]` is keyed by the question's FormKit `name`.
 */

export const NOT_PROVIDED = 'Not Provided';

/** Step of the intake form holding the applicant's date of birth (same id as in the audit summary) */
const APPLICANT_DETAILS_STEP_ID = '66539288-bb83-4be7-a723-8e3170a0e812';

export interface SummaryLine {
  id: string;
  label: string;
  value: string;
  kind?: 'text' | 'signature';
  /** Signature lines: data URL of the applicant's signature (resolved from `blobId` when needed) */
  image?: string;
  /** Signature lines: blob id of the image when the answer doesn't carry the data URL itself */
  blobId?: string;
  /** Signature lines: IP address / date shown under the image */
  details?: { label: string; value: string }[];
}

export const findFormStep = (application: any, form: CustomForm) =>
  application?.formkitForm?.steps?.find((s: any) => s.stepId === form.formStepId);

export const formAnswers = (application: any, form: CustomForm): Record<string, any> =>
  findFormStep(application, form)?.data?.[0] ?? {};

const isEmpty = (v: any) =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

// ---------------------------------------------------------------------------
// Signature answers
// ---------------------------------------------------------------------------

/** Signature answer as stored, plus the blob id when the image itself is not inlined */
export type NormalizedSignature = SignatureAnswer;

const isDataUrl = (v: string) => /^data:/i.test(v);
/** Base64 payload without the `data:` prefix (a blob id / GUID never matches: too short, has dashes) */
const isBareBase64 = (v: string) => /^[A-Za-z0-9+/]{100,}={0,2}$/.test(v);
const withDataPrefix = (v: string) => (isBareBase64(v) ? `data:image/png;base64,${v}` : v);
const firstString = (...candidates: any[]) => {
  const found = candidates.find((c) => typeof c === 'string' && c.trim() !== '');
  return found ? `${found}`.trim() : '';
};

/**
 * Tolerant reader of a signature answer: the `{ image, userIp, timestamp }` object saved by sa-help,
 * the same object as a JSON string, a bare data URL / base64 payload, or a blob id (to be fetched).
 * Returns null when there is no signature at all.
 */
export const normalizeSignature = (raw: any): NormalizedSignature | null => {
  if (raw === undefined || raw === null || raw === '') return null;
  if (Array.isArray(raw)) return normalizeSignature(raw[0]);

  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return null;
    if (s.startsWith('{')) {
      try {
        return normalizeSignature(JSON.parse(s));
      } catch {
        return null;
      }
    }
    const image = withDataPrefix(s);
    if (isDataUrl(image)) return { image, userIp: '', timestamp: '' };
    return { image: '', userIp: '', timestamp: '', blobId: s };
  }

  if (typeof raw !== 'object') return null;
  const o = raw;
  let image = withDataPrefix(firstString(o.image, o.signature, o.dataUrl, o.data, o.file));
  let blobId = firstString(o.blobId, o.url, o.fileId);
  if (image && !isDataUrl(image)) {
    // a reference to the stored file rather than the picture itself
    blobId = blobId || image;
    image = '';
  }
  if (!image && !blobId) return null;
  return {
    image,
    userIp: firstString(o.userIp, o.remoteIP, o.ip),
    timestamp: firstString(o.timestamp, o.createdAt, o.date),
    ...(blobId ? { blobId } : {}),
    ...(firstString(o.browser) ? { browser: firstString(o.browser) } : {}),
    ...(firstString(o.device) ? { device: firstString(o.device) } : {}),
  };
};

/** `M/d/yyyy hh:mm a (ZZZZ)` like the consent page, or the raw value when it isn't an ISO date */
export const formatSignatureDate = (ts: string): string => {
  if (!ts) return '';
  const dt = DateTime.fromISO(ts);
  return dt.isValid ? dt.toFormat('M/d/yyyy hh:mm a (ZZZZ)') : ts;
};

const signatureAnswer = (field: CustomFormField, answers: Record<string, any>) =>
  normalizeSignature(answers?.[field.formkitSchema?.name]);

/**
 * Every blob id referenced by a "signature" question answer anywhere in the application's steps (a
 * Custom Form's signature question can land on any step, not just ones belonging to one specific form).
 * Used to resolve every signature image up front — e.g. before print.vue triggers `window.print()`.
 */
export const collectSignatureBlobIds = (application: any): string[] => {
  const ids = new Set<string>();
  (application?.formkitForm?.steps ?? []).forEach((step: any) => {
    const signatureFieldNames = (step.fields ?? []).filter((f: any) => f.$formkit === 'signature').map((f: any) => f.name);
    if (!signatureFieldNames.length) return;
    (step.data ?? []).forEach((row: any) => {
      signatureFieldNames.forEach((name: string) => {
        const blobId = normalizeSignature(row?.[name])?.blobId;
        if (blobId) ids.add(blobId);
      });
    });
  });
  return [...ids];
};

/** Strict check used before an answer is injected in markup: PNG/JPEG data URL made of base64 only */
const isSafeImageDataUrl = (v: string) => /^data:image\/(png|jpe?g);base64,[A-Za-z0-9+/]+=*$/i.test(v);
const escapeHtml = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** A SAS url handed back by our own file-storage endpoint (`getSasDocument`), safe to drop into `src` */
const isSafeImageUrl = (v: string) => /^https:\/\//i.test(v);

/**
 * HTML for a signature answer in views rendered with `v-html` (application show / print pages):
 * the image plus the IP address and date. '' when there is no signature. When the answer only holds
 * a blob id (the applicant's image is stored in blob storage, not inlined), pass the SAS url already
 * resolved for that id as `resolvedImageUrl` (see `showFieldValue` in show.vue/print.vue) — without
 * it, a text fallback ("Signed") is shown instead of the picture.
 */
export const signatureAnswerHtml = (raw: any, resolvedImageUrl?: string): string => {
  const sig = normalizeSignature(raw);
  if (!sig) return '';
  const details = [
    { label: 'IP Address', value: sig.userIp },
    { label: 'Date', value: formatSignatureDate(sig.timestamp) },
    { label: 'Browser', value: sig.browser ?? '' },
    { label: 'Device', value: sig.device ?? '' },
  ]
    .filter((d) => d.value)
    .map((d) => `<div class="text-xs">${d.label}: <b>${escapeHtml(d.value)}</b></div>`)
    .join('');
  const src = isSafeImageDataUrl(sig.image ?? '')
    ? sig.image!
    : isSafeImageUrl(resolvedImageUrl ?? '')
    ? resolvedImageUrl!
    : '';
  const image = src
    ? `<img src="${escapeHtml(
        src
      )}" alt="Signature" class="w-[200px] h-[70px] border border-gray-300 object-contain bg-white" />`
    : '<span class="italic">Signed</span>';
  return `${image}${details}`;
};

// ---------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------

/**
 * Human-readable answer for one question. Option-based questions are resolved to their labels
 * (options from the application step when present, else from the form's schema), files to their
 * name, dates to MM/DD/YYYY, signatures to "Signed" + date (text-only contexts).
 */
/** The FormKit mask of the currency questions (intake and custom form): the answer is saved in cents */
const CURRENCY_MASK = '{#,##|repeat}#.##';
const isMoneySchema = (schema: any) => schema?.mask === CURRENCY_MASK && schema?.prefix === '$';

export const formatAnswer = (field: CustomFormField, answers: Record<string, any>, stepFields: any[] = []): string => {
  const schema = field.formkitSchema;
  const raw = answers?.[schema?.name];
  // a check box left empty is an answer too (saved as '')
  if (isCheckboxField(schema)) return isCheckedAnswer(raw) ? 'Yes' : 'No';
  if (isEmpty(raw)) return NOT_PROVIDED;

  if (isSignatureField(schema)) {
    const sig = normalizeSignature(raw);
    if (!sig) return NOT_PROVIDED;
    const signedAt = formatSignatureDate(sig.timestamp);
    return signedAt ? `Signed ${signedAt}` : 'Signed';
  }

  // currency question ({#,##|repeat}#.## mask, $ prefix): cents → "$1,234.56" (the schema stored with the
  // step is checked too, in case the form's own copy lost a property)
  const stepField = stepFields.find((f) => f.name === schema?.name);
  if (isMoneySchema(schema) || isMoneySchema(stepField)) {
    const cents = `${raw}`.replace(/[^0-9-]/g, '');
    if (cents !== '' && cents !== '-') return `${formatNumberToMoney(convertFromCents(cents))}`;
  }

  const options = stepField?.options ?? schema.options;
  if (Array.isArray(options) && options.length) {
    const values = Array.isArray(raw) ? raw : [raw];
    const labels = options.filter((o: any) => values.includes(o.value)).map((o: any) => o.label);
    if (labels.length) return labels.join(', ');
  }

  if (Array.isArray(raw)) {
    // file uploads are stored as [{ name, ... }]
    return (
      raw
        .map((r: any) => (r && typeof r === 'object' ? r.name ?? '' : `${r}`))
        .filter(Boolean)
        .join(', ') || NOT_PROVIDED
    );
  }
  if (raw && typeof raw === 'object') return raw.name ?? JSON.stringify(raw);

  if (schema?.$formkit === 'date') return formatDateForFilters(`${raw}`) || `${raw}`;
  return `${raw}`;
};

/** One `label: value` line per question, in display order (Block Text / Empty Line are skipped) */
export const buildSummaryLines = (form: CustomForm, application: any): SummaryLine[] => {
  const answers = formAnswers(application, form);
  const stepFields = findFormStep(application, form)?.fields ?? [];
  return [...(form.fields ?? [])]
    .filter((f) => f.active && !isDisplayOnlyField(f.formkitSchema))
    .sort((a, b) => effectiveDisplayOrder(a) - effectiveDisplayOrder(b))
    .map((f): SummaryLine => {
      const label = f.formkitSchema?.label ?? f.name;
      if (!isSignatureField(f.formkitSchema)) return { id: f.id, label, value: formatAnswer(f, answers, stepFields) };

      const sig = signatureAnswer(f, answers);
      return {
        id: f.id,
        label,
        kind: 'signature',
        value: sig ? 'Signed' : NOT_PROVIDED,
        image: sig?.image ?? '',
        blobId: sig?.blobId,
        details: [
          { label: 'IP Address', value: sig?.userIp ?? '' },
          { label: 'Date', value: formatSignatureDate(sig?.timestamp ?? '') },
          { label: 'Browser', value: sig?.browser ?? '' },
          { label: 'Device', value: sig?.device ?? '' },
        ].filter((d) => d.value),
      };
    });
};

export const applicantFullName = (applicant: any) =>
  [applicant?.firstName, applicant?.middleName, applicant?.lastName].filter((p) => p && `${p}`.trim()).join(' ');

export const applicantAddress = (applicant: any) => {
  const street = [applicant?.address1, applicant?.address2].filter((p) => p && `${p}`.trim()).join(', ');
  const cityLine = [applicant?.city, [applicant?.state, applicant?.zipcode].filter(Boolean).join(' ')]
    .filter((p) => p && `${p}`.trim())
    .join(', ');
  return [street, cityLine].filter(Boolean).join(', ');
};

export const applicantDateOfBirth = (application: any) => {
  const dob =
    application?.applicant?.dateOfBirth ??
    application?.formkitForm?.steps?.find((s: any) => s.stepId === APPLICANT_DETAILS_STEP_ID)?.data?.[0]?.dob;
  if (!dob) return '';
  return formatDateForFilters(`${dob}`.slice(0, 10)) || `${dob}`;
};

/** `MM/dd/yyyy`, or '' when the value isn't a valid ISO date (missing/not-yet-reached, e.g. no approval yet) */
export const formatMetaDate = (iso: any): string => {
  const dt = DateTime.fromISO(`${iso ?? ''}`);
  return dt.isValid ? dt.toFormat('MM/dd/yyyy') : '';
};

/**
 * Values for every mapper field (system defaults + the form's questions), keyed like the placements.
 * Signature questions hold the image data URL — or a blob id, see `resolveSignatureImages`.
 * `ipAddress` / `browserType` / `browserDevice` are left blank here — see `resolveApplicationMetaValues`
 * for those, since they need their own request. `acceptedDate` is always blank: the API has no
 * corresponding data yet (see the comment on `SYSTEM_MAPPER_FIELDS`).
 */
export const buildPdfValues = (form: CustomForm, application: any): Record<string, string> => {
  const applicant = application?.applicant ?? {};
  const values: Record<string, string> = {
    applicantName: applicantFullName(applicant),
    applicantAddress: applicantAddress(applicant),
    applicantPhone: formatPhoneNumber(applicant?.phoneNumber ?? '') || applicant?.phoneNumber || '',
    applicantEmail: applicant?.emailAddress ?? '',
    applicantDateOfBirth: applicantDateOfBirth(application),
    generationDate: DateTime.now().toFormat('MM/dd/yyyy'),
    applicationDate: formatMetaDate(application?.createdAt),
    submissionDate: formatMetaDate(application?.submittedAt),
    acceptedDate: '',
    ipAddress: '',
    browserType: '',
    browserDevice: '',
  };
  const answers = formAnswers(application, form);
  const stepFields = findFormStep(application, form)?.fields ?? [];
  (form.fields ?? []).forEach((f) => {
    const key = f.formkitSchema?.name || f.name || f.id;
    // a Block Text placed on the PDF prints its own text
    if (f.formkitSchema?.$formkit === BLOCK_TEXT_TYPE) {
      values[key] = blockTextContent(f.formkitSchema?.helpLong).trim();
      return;
    }
    if (isDisplayOnlyField(f.formkitSchema)) return;
    if (isSignatureField(f.formkitSchema)) {
      const sig = signatureAnswer(f, answers);
      values[key] = sig?.image || sig?.blobId || '';
      return;
    }
    // a check box placement draws a check mark only when ticked (see `fillPdf`)
    if (isCheckboxField(f.formkitSchema)) {
      values[key] = isCheckedAnswer(answers?.[f.formkitSchema?.name]) ? CHECKBOX_ON_VALUE : '';
      return;
    }
    const value = formatAnswer(f, answers, stepFields);
    values[key] = value === NOT_PROVIDED ? '' : value;
  });
  return values;
};

const fetchImage = async (blobId: string, fetchBlob: (blobId: string) => Promise<string>) => {
  try {
    return (await fetchBlob(blobId)) || '';
  } catch (e) {
    console.warn(`unable to load the signature image ${blobId}`, e);
    return '';
  }
};

/**
 * Replaces, in place, the signature values that are blob ids (not data URLs) by the image fetched
 * through `fetchBlob` — '' when it cannot be loaded, so nothing is drawn instead of the id.
 */
export const resolveSignatureImages = async (
  form: CustomForm,
  values: Record<string, string>,
  fetchBlob: (blobId: string) => Promise<string>
): Promise<Record<string, string>> => {
  const pending = (form.fields ?? [])
    .filter((f) => isSignatureField(f.formkitSchema))
    .map((f) => f.formkitSchema?.name || f.name || f.id)
    .filter((key) => values[key] && !isDataUrl(values[key]));
  await Promise.all(
    pending.map(async (key) => {
      values[key] = await fetchImage(values[key], fetchBlob);
    })
  );
  return values;
};

export interface ApplicationConsentRecord {
  remoteIP?: string;
  userAgent?: string;
  description?: string;
}

const detectBrowserType = (ua: string): string => {
  if (/edg(a|ios|e)?\//i.test(ua)) return 'Edge';
  if (/opr\/|opera/i.test(ua)) return 'Opera';
  if (/firefox|fxios/i.test(ua)) return 'Firefox';
  if (/chrome|crios/i.test(ua)) return 'Chrome';
  if (/safari/i.test(ua)) return 'Safari';
  return 'Unknown';
};

const detectBrowserDevice = (ua: string): string => {
  if (/ipad/i.test(ua)) return 'iPad';
  if (/iphone/i.test(ua)) return 'iPhone';
  if (/ipod/i.test(ua)) return 'iPod';
  if (/android/i.test(ua)) return 'Android';
  if (/windows phone/i.test(ua)) return 'Windows Phone';
  if (/windows/i.test(ua)) return 'Windows';
  if (/macintosh|mac os x/i.test(ua)) return 'Mac';
  if (/linux/i.test(ua)) return 'Linux';
  return 'Unknown';
};

/**
 * Fills `values.ipAddress` / `browserType` / `browserDevice` from the application's own consent record
 * (`GET /applications/{id}/consent`) — it can hold several rows (main consent, FPL consent, ...), so
 * this takes the first one whose `description` is "consent page" (the main application consent).
 * All three stay '' when there's no such row, no `userAgent` on it, or the request fails.
 */
export const resolveApplicationMetaValues = async (
  values: Record<string, string>,
  applicationId: string,
  fetchConsent: (applicationId: string) => Promise<ApplicationConsentRecord[] | null | undefined>
): Promise<Record<string, string>> => {
  if (!('ipAddress' in values)) return values;
  try {
    const records = (await fetchConsent(applicationId)) ?? [];
    const consent = records.find((r) => r.description === 'consent page');
    values.ipAddress = consent?.remoteIP ?? '';
    const ua = consent?.userAgent ?? '';
    values.browserType = ua ? detectBrowserType(ua) : '';
    values.browserDevice = ua ? detectBrowserDevice(ua) : '';
  } catch (e) {
    console.warn(`unable to load the consent record for application ${applicationId}`, e);
  }
  return values;
};

/** Same as `resolveSignatureImages` for the summary lines of the generic PDF */
export const resolveSummaryLineImages = async (
  lines: SummaryLine[],
  fetchBlob: (blobId: string) => Promise<string>
): Promise<SummaryLine[]> => {
  await Promise.all(
    lines
      .filter((line) => line.kind === 'signature' && !line.image && line.blobId)
      .map(async (line) => {
        line.image = await fetchImage(line.blobId as string, fetchBlob);
      })
  );
  return lines;
};

export const pdfFieldsFor = (
  form: CustomForm,
  allSteps: FormFieldFormStep[] = [],
  territoryRequiredByLinkId: Record<string, boolean> = {},
  customQuestionLinkIds: Set<string> = new Set()
): MapperField[] => buildMapperFields(form, allSteps, territoryRequiredByLinkId, customQuestionLinkIds);

/**
 * Fills the values of "Application Questions" mapper fields (`aq:{stepId}:{fieldName}`, see
 * `buildApplicationQuestionFields`) from the applicant's answer to that step.
 *
 * An assistance type's own questions (e.g. the "Food" step) are answered on the assistance request, not
 * on the application, so `assistanceRequests` (each `/assistance-requests/{id}` of the application's
 * `assistanceRequestIDs`) are searched too: the first step with that id that actually holds an answer wins.
 */
export const resolveApplicationQuestionValues = (
  values: Record<string, string>,
  fields: MapperField[],
  application: any,
  assistanceRequests: any[] = []
): Record<string, string> => {
  const allSteps: any[] = [application, ...assistanceRequests].flatMap((src) => src?.formkitForm?.steps ?? []);
  fields.forEach((f) => {
    const parsed = parseApplicationQuestionKey(f.key);
    if (!parsed) return;
    const candidates = allSteps.filter((s: any) => s.stepId === parsed.stepId);
    const step = candidates.find((s: any) => !isEmpty(s?.data?.[0]?.[parsed.fieldName])) ?? candidates[0];
    const answers = step?.data?.[0] ?? {};
    const raw = answers?.[parsed.fieldName];
    if (isEmpty(raw)) {
      values[f.key] = '';
      return;
    }
    const schema = (step?.fields ?? []).find((sf: any) => sf.name === parsed.fieldName) ?? { name: parsed.fieldName };
    const value = formatAnswer({ formkitSchema: schema } as any, answers, step?.fields ?? []);
    values[f.key] = value === NOT_PROVIDED ? '' : value;
  });
  return values;
};
